import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { graphqlApi } from '../../../services/graphql';
import { useAuthStore } from '../../../store/authStore';
import { INTEREST_CATEGORIES } from '../../../constants/categories';
import { COUNTRIES } from '../../../constants/countries';
import AddressPicker, {
  AddressPickerValue,
  StructuredExtras,
} from '../../common/AddressPicker';
import toast from 'react-hot-toast';

interface BusinessFormState {
  companyName: string;
  industry: string;
  country: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  latitude: number | null;
  longitude: number | null;
  registrationNumber: string;
  registrationVerified: boolean;
  registrationFileUrl: string;
  contactPhone: string;
  contactPhoneVerified: boolean;
  contactEmail: string;
}

const HostBusinessProfilePage: React.FC = () => {
  const { t } = useTranslation('profile');
  const { user } = useAuthStore();

  const [form, setForm] = useState<BusinessFormState>({
    companyName: '', industry: '', country: '', addressLine1: '', addressLine2: '',
    city: '', state: '', postalCode: '', latitude: null, longitude: null,
    registrationNumber: '', registrationVerified: false,
    registrationFileUrl: '', contactPhone: '', contactPhoneVerified: false, contactEmail: '',
  });
  const [hostId, setHostId] = useState<string | null>(null);
  const [contactCountryCode, setContactCountryCode] = useState('+82');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const fetchData = async () => {
      if (!user?.userId) { setLoading(false); return; }
      try {
        const res = await graphqlApi.getHostProfileByUserId(user.userId);
        if (res.success && res.data) {
          const h = res.data;
          setHostId(h.hostId);
          setForm({
            companyName: h.companyName || '', industry: h.industry || '', country: h.country || '',
            addressLine1: h.addressLine1 || h.location || '', addressLine2: h.addressLine2 || '',
            city: h.city || '', state: h.state || '', postalCode: h.postalCode || '',
            latitude: h.latitude ?? null, longitude: h.longitude ?? null,
            registrationNumber: h.registrationNumber || '', registrationVerified: h.registrationVerified || false,
            registrationFileUrl: h.registrationFileUrl || '',
            contactPhone: h.contactPhone || '', contactPhoneVerified: h.contactPhoneVerified || false,
            contactEmail: user.email || '',
          });
        } else {
          setForm(prev => ({ ...prev, contactEmail: user.email || '' }));
        }
      } catch (err) { console.error('Failed to fetch host profile:', err); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [user?.userId, user?.email]);

  const handleChange = (field: keyof BusinessFormState, value: string | boolean | number | null) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: false }));
  };

  const handleAddressPickerChange = useCallback(
    (next: AddressPickerValue, extras?: StructuredExtras) => {
      setForm(prev => {
        const merged: BusinessFormState = {
          ...prev,
          addressLine1: next.text,
          latitude: next.latitude,
          longitude: next.longitude,
        };
        // `extras` is only emitted when the user selects a place from the
        // autocomplete dropdown. In that case we REPLACE the structured
        // fields wholesale (including clearing them) so stale values from
        // the previous address don't leak through. We only refuse to stomp
        // the country select when the parsed ISO code isn't a known option
        // — otherwise the select would render an invalid value.
        if (extras) {
          merged.city = extras.city;
          merged.state = extras.state;
          merged.postalCode = extras.postalCode;
          if (extras.country && COUNTRIES.includes(extras.country as typeof COUNTRIES[number])) {
            merged.country = extras.country;
          }
        }
        return merged;
      });
      // Clear any red-error borders on the fields we just (re)populated.
      setErrors(prev => ({
        ...prev,
        addressLine1: false,
        city: false,
        state: false,
        postalCode: false,
        country: false,
      }));
    },
    [],
  );

  const validate = () => {
    const required: (keyof BusinessFormState)[] = ['companyName', 'industry', 'country', 'addressLine1', 'city', 'postalCode', 'registrationNumber'];
    const newErrors: Record<string, boolean> = {};
    required.forEach(f => { if (!form[f]) newErrors[f] = true; });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) { toast.error(t('myProfile.hostBusiness.fillRequired')); return; }
    if (!hostId) { toast.error('Host profile not found'); return; }
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        companyName: form.companyName, industry: form.industry, country: form.country,
        addressLine1: form.addressLine1, addressLine2: form.addressLine2,
        city: form.city, state: form.state, postalCode: form.postalCode,
        registrationNumber: form.registrationNumber, contactPhone: form.contactPhone,
      };
      if (form.latitude !== null || form.longitude !== null) {
        payload.latitude = form.latitude;
        payload.longitude = form.longitude;
      }
      await graphqlApi.updateHostProfile(hostId, payload as any);
      toast.success(t('myProfile.hostBusiness.saved'));
    } catch { toast.error(t('myProfile.hostBusiness.saveFailed')); }
    finally { setSaving(false); }
  };

  const handleVerifyRegistration = () => { toast.success('사업자등록번호 인증 - coming soon'); };
  const handleUploadFile = () => { toast.success('파일 업로드 - coming soon'); };
  const handleRequestApproval = () => { toast.success('승인요청 - coming soon'); };
  const handleVerifyPhone = () => { toast.success('연락처 인증 - coming soon'); };

  if (loading) {
    return <div className="flex justify-center items-center min-h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>;
  }

  const inputClass = (field: string) =>
    `w-full px-3 py-2 text-sm border rounded-lg focus:ring-1 focus:outline-none ${
      errors[field] ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
    }`;

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-8">
      {/* 비즈니스 정보 */}
      <section className="pt-6">
        <h2 className="text-2xl font-semibold text-gray-800 mb-2">{t('myProfile.hostBusiness.businessInfo')}</h2>
        <hr className="border-gray-200 mb-4" />
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('myProfile.hostBusiness.companyName')} <span className="text-red-500">*</span></label>
            <input type="text" value={form.companyName} onChange={e => handleChange('companyName', e.target.value)} className={inputClass('companyName')} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('myProfile.hostBusiness.industry')} <span className="text-red-500">*</span></label>
            <select value={form.industry} onChange={e => handleChange('industry', e.target.value)} className={inputClass('industry')}>
              <option value="">{t('myProfile.hostBusiness.selectIndustry')}</option>
              {INTEREST_CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{t(`common:categories.${cat}`)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('myProfile.hostBusiness.country')} <span className="text-red-500">*</span></label>
            <select value={form.country} onChange={e => handleChange('country', e.target.value)} className={inputClass('country')}>
              <option value="">{t('myProfile.hostBusiness.selectCountry')}</option>
              {COUNTRIES.map(code => (
                <option key={code} value={code}>{t(`common:countries.${code}`)}</option>
              ))}
            </select>
          </div>
          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('myProfile.hostBusiness.companyAddress')} <span className="text-red-500">*</span></label>
            <div className="space-y-2">
              <AddressPicker
                mode="structured"
                value={{
                  text: form.addressLine1,
                  latitude: form.latitude,
                  longitude: form.longitude,
                }}
                onChange={handleAddressPickerChange}
                hasError={errors.addressLine1}
                placeholder="Address Line 1"
              />
              <input type="text" value={form.addressLine2} onChange={e => handleChange('addressLine2', e.target.value)} placeholder="Address Line 2" className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:outline-none focus:ring-blue-500" />
              <div className="grid grid-cols-2 gap-2">
                <input type="text" value={form.city} onChange={e => handleChange('city', e.target.value)} placeholder="City / District" className={inputClass('city')} />
                <input type="text" value={form.state} onChange={e => handleChange('state', e.target.value)} placeholder="State / Province" className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:outline-none focus:ring-blue-500" />
              </div>
              <input type="text" value={form.postalCode} onChange={e => handleChange('postalCode', e.target.value)} placeholder="Postal Code" className={inputClass('postalCode')} />
            </div>
          </div>

          {/* 사업자등록번호 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('myProfile.hostBusiness.registrationNumber')}</label>
            <input type="text" value={form.registrationNumber} onChange={e => handleChange('registrationNumber', e.target.value)} className={inputClass('registrationNumber')} />
          </div>
          {/* 사업자등록증 업로드 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('myProfile.hostBusiness.registrationUpload')}</label>
            <div className="flex gap-2 items-center">
              <button type="button" onClick={handleUploadFile} className="px-4 py-2 text-sm font-medium text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50">
                {t('myProfile.hostBusiness.attachFile')}
              </button>
              {form.registrationFileUrl && <span className="text-sm text-green-600">✓ Uploaded</span>}
            </div>
          </div>
        </div>
      </section>

      {/* 담당자 정보 */}
      <section>
        <h2 className="text-2xl font-semibold text-gray-800 mb-2">{t('myProfile.hostBusiness.contactInfo')}</h2>
        <hr className="border-gray-200 mb-4" />
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('myProfile.hostBusiness.contactPhone')} <span className="text-red-500">*</span></label>
            <div className="flex gap-0">
              <select value={contactCountryCode} onChange={e => setContactCountryCode(e.target.value)}
                className="min-w-[6rem] px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-300 rounded-l-lg focus:ring-1 focus:outline-none focus:ring-blue-500 focus:border-blue-500">
                <option value="+82">🇰🇷 +82</option>
                <option value="+1">🇺🇸 +1</option>
                <option value="+81">🇯🇵 +81</option>
                <option value="+86">🇨🇳 +86</option>
                <option value="+44">🇬🇧 +44</option>
                <option value="+49">🇩🇪 +49</option>
                <option value="+33">🇫🇷 +33</option>
                <option value="+61">🇦🇺 +61</option>
                <option value="+65">🇸🇬 +65</option>
                <option value="+84">🇻🇳 +84</option>
                <option value="+66">🇹🇭 +66</option>
                <option value="+63">🇵🇭 +63</option>
                <option value="+62">🇮🇩 +62</option>
                <option value="+60">🇲🇾 +60</option>
                <option value="+91">🇮🇳 +91</option>
              </select>
              <input type="tel" value={form.contactPhone} onChange={e => handleChange('contactPhone', e.target.value.replace(/\D/g, ''))}
                className="flex-1 px-3 py-2 text-sm border-y border-r border-gray-300 rounded-r-lg focus:ring-1 focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('myProfile.hostBusiness.email')}</label>
            <div className="flex items-center gap-2">
              <input type="email" value={form.contactEmail} readOnly className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed" />
              <span className="text-green-600 text-sm">✓</span>
            </div>
          </div>
        </div>
      </section>

      {/* 저장 */}
      <button type="button" onClick={handleSave} disabled={saving}
        className="w-full sm:w-auto px-6 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
        {saving ? '...' : t('myProfile.hostBusiness.save')}
      </button>
    </div>
  );
};

export default HostBusinessProfilePage;
